import type { MultiPayload } from "@defuse-protocol/contract-types"
import { AuthMethod } from "@defuse-protocol/internal-utils"
import { nearClient } from "@src/components/DefuseSDK/constants/nearClient"
import { publicKeyVerifierMachine } from "@src/components/DefuseSDK/features/machines/publicKeyVerifierMachine"
import type { AuthJourneyFailure } from "@src/features/auth/analytics/authJourneyLease"
import {
  type FailedNearSignature,
  type WalletVerificationOutput,
  walletVerificationMachine,
} from "@src/features/auth/machines/walletVerificationMachine"
import { logger } from "@src/utils/logger"
import {
  type ActorRefFrom,
  assign,
  emit,
  fromCallback,
  fromPromise,
  sendParent,
  sendTo,
  setup,
} from "xstate"

export interface AuthContext {
  address: string | null
  chainType: string | null
  nearAccount: { accountId: string; publicKey: string } | null
  cachedSignedData: MultiPayload | null
}

export type AuthEvent =
  | {
      type: "WALLET_STATE"
      address: string | null
      chainType: string | null
      restoreStatus: "restoring" | "settled"
    }
  | { type: "START_VERIFICATION"; address: string; chainType: string }
  | { type: "ABORT_VERIFICATION" }
  | { type: "BYPASS_SAFETY" }
  | { type: "PROCEED_UNVERIFIED" }
  | { type: "RETRY_SAFETY" }
  | { type: "ABORT_GATING" }
  | { type: "AUTH_INVALIDATED" }

export type AuthEmittedEvent = {
  type: "WALLET_DISCONNECT_REQUESTED"
  chainType: string
}

export type AuthMachineRef = ActorRefFrom<typeof authMachine>

/**
 * Authentication lifecycle machine used by bootstrap.
 *
 * Top-level states:
 * - `restoring`   : wait for wallet restore settlement.
 * - `gating`      : ban + safety checks.
 * - `ready`       : wallet/auth baseline is settled.
 * - `authenticating` : signature + NEAR pubkey flow.
 * - `blocked`     : terminal parking after user aborts gated wallet.
 *
 * Parent contract:
 * - Sends: `WALLET_RESTORED`, `AUTH_INVALIDATED`, `BANNED_DETECTED`,
 *   `VERIFICATION_COMPLETE`, `SIGN_OUT`.
 * - Emits (via `actor.on(...)`): `WALLET_DISCONNECT_REQUESTED`.
 *
 * Side effects are injected in provider via `.provide()`.
 */
export const authMachine = setup({
  types: {} as {
    context: AuthContext
    events: AuthEvent
    emitted: AuthEmittedEvent
  },
  actors: {
    appWalletVerification: walletVerificationMachine,
    appPubKeyVerifier: publicKeyVerifierMachine,
    authInvalidationListener: fromCallback<{ type: "AUTH_INVALIDATED" }>(
      ({ sendBack }) => {
        if (typeof window === "undefined") return
        const handler = () => sendBack({ type: "AUTH_INVALIDATED" })
        window.addEventListener("auth:invalidated", handler)
        return () => window.removeEventListener("auth:invalidated", handler)
      }
    ),
    banService: fromPromise(
      async (_: {
        input: { address: string; chainType: string }
      }): Promise<{ isBanned: boolean }> => {
        throw new Error("banService must be provided via .provide()")
      }
    ),
    safetyService: fromPromise(
      async (_: {
        input: { address: string; chainType: string }
      }): Promise<{ status: "safe" | "unsafe" }> => {
        throw new Error("safetyService must be provided via .provide()")
      }
    ),
    reauthAfterPubkey: fromPromise(
      async (_: {
        input: {
          signedData: MultiPayload
          address: string
          chainType: AuthMethod
        }
      }): Promise<{ success: boolean }> => {
        throw new Error("reauthAfterPubkey must be provided via .provide()")
      }
    ),
  },
  guards: {
    isSettled: ({ event }) =>
      event.type === "WALLET_STATE" && event.restoreStatus === "settled",
    hasWalletInEvent: ({ event }) =>
      event.type === "WALLET_STATE" && event.address != null,
    isVerified: (_, params: WalletVerificationOutput) => params.verified,
    hasFailedNearSignature: (_, params: WalletVerificationOutput) =>
      !params.verified && params.failedNearSignature != null,
    isPubKeyOk: (_, params: { tag: "ok" | "err"; value?: unknown }) =>
      params.tag === "ok",
    isReauthSuccess: (_, params: { success: boolean }) =>
      params.success === true,
    isBanned: (_, params: { output: { isBanned: boolean } }) =>
      params.output.isBanned,
    isUnsafe: (_, params: { output: { status: "safe" | "unsafe" } }) =>
      params.output.status === "unsafe",
  },
  actions: {
    setWalletFromEvent: assign(({ event }) => {
      if (event.type !== "WALLET_STATE") return {}
      return { address: event.address, chainType: event.chainType }
    }),
    setVerificationTarget: assign(({ event }) => {
      if (event.type !== "START_VERIFICATION") return {}
      return { address: event.address, chainType: event.chainType }
    }),
    captureFailedNearSignature: assign((_, params: FailedNearSignature) => ({
      nearAccount: {
        accountId: params.accountId,
        publicKey: params.publicKey,
      },
      cachedSignedData: params.signedData,
    })),
    clearPubkeyRecoveryState: assign({
      nearAccount: () => null,
      cachedSignedData: () => null,
    }),
    clearWallet: assign({
      address: () => null,
      chainType: () => null,
      nearAccount: () => null,
      cachedSignedData: () => null,
    }),
    logRestoreTimeout: () => {
      logger.warn("[authMachine] wallet restore timed out")
    },
    logGatingTimeout: () => {
      logger.warn(
        "[authMachine] ban check timed out — proceeding to safety check"
      )
    },
    logBanCheckUnavailable: (_, params: { error: unknown }) => {
      logger.warn(
        "[authMachine] ban check unavailable — proceeding to safety check",
        {
          error: params.error,
        }
      )
    },
    logSafetyCheckUnavailable: () => {
      logger.warn(
        "[authMachine] safety check unavailable — routing to safety_unreachable"
      )
    },
    logWalletVerificationFailed: () => {
      logger.warn("[authMachine] wallet verification failed — signing out")
    },
    logPubkeyVerificationFailed: () => {
      logger.warn(
        "[authMachine] pubkey verification failed — signing out and requesting wallet disconnect"
      )
    },
    logReauthFailed: (_, params?: { error?: unknown }) => {
      logger.warn(
        "[authMachine] reauth after pubkey recovery failed — signing out and requesting wallet disconnect",
        params?.error !== undefined ? { error: params.error } : undefined
      )
    },
    sendParentWalletRestored: sendParent(({ context }) => ({
      type: "WALLET_RESTORED" as const,
      address: context.address,
      chainType: context.chainType,
    })),
    sendParentAuthInvalidated: sendParent({
      type: "AUTH_INVALIDATED" as const,
    }),
    sendParentVerificationComplete: sendParent({
      type: "VERIFICATION_COMPLETE" as const,
    }),
    sendParentSignOut: sendParent({ type: "SIGN_OUT" as const }),
    clearAuthJourneyLease: () => {},
    failAuthJourney: (_, _failure: AuthJourneyFailure) => {},
    sendParentBannedDetected: sendParent({
      type: "BANNED_DETECTED" as const,
    }),
    requestWalletDisconnect: emit(({ context }) => {
      if (context.chainType == null) {
        throw new Error(
          "requestWalletDisconnect fired without a chainType — guards should have prevented this"
        )
      }
      return {
        type: "WALLET_DISCONNECT_REQUESTED" as const,
        chainType: context.chainType,
      }
    }),
  },
  delays: {
    RESTORE_TIMEOUT: 10_000,
    // Per-check ceiling on ban/safety gating. The onError paths fail open on
    // rejection, but a hung fetch (no response) never rejects — without this
    // the app would park on the splash forever. Timing out fails open too.
    GATING_TIMEOUT: 10_000,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QEMCuAXAFgYgIIFUAVACQH0BJAOQDVcAZcgEV0IFFGBtABgF1FQADgHtYAS3SihAO34gAHogCMAJgDsXAHQBmLQFYAnAA4u+gGz7li3aYA0IAJ6IAtMt0bTarqqunDWxVqq+roAviF2aFgaAE5w6ELRolJQ2ADq9HSshKQAyoQsrNx8SCDCYhLSsgoIiqqmGoa6PqYALIYtJob6do41HsoaAfpeqspqY+1hERiYMXEJSSnpdJnZeQUcisWCIuKSMiXViqb1jc1tHUbdDohj+hrB-i2mWmZayieqUyCRs7Gw8USyWwcgByHQYA0yAAZhDogAKABKrDyAHlkaRCOQALKsVFEACU2F+cwBC2SRVkZT2lUOSjqDSa1la7U6116yi4gQ0uleii5ihaukMpmO3xJUHBizSGSyuXybEpJWpFQOoCOgXq+iMWhMplUQXethuNR0qgaKl0nP0zy4gvFMw0kokyQ0AGNMGA3QBrRakABGyCk2Ag0khSQAbkJvZCJVLXR6vb7kgGgwhI0I3VLpEUlTtyvsqohvJplFoWlpjMp9P5DIYevTdOamS9dMKK35QuEfo7nYt3Z6fX7A8HQ1Jw1IozGob341AB0nh2mM1nVbmtlTdqqiwgSxoyx2uNXa-WTbV+Ro7dY9G3VHWRQ6on2E4Pk1BU8GwNFogkNAIADbgtCCQALYzk+c4LkOKYjumk6ZtmUi5rwm4FrS6rFheB6VkeNaKHWDZ9Co7g+LULRGKYwqPrMz7zom0HviOIJghCUKwl+8IAOIsFQnGYjieKEsSs4unRr5LkhKHKluhZ0ruWHljhx74aevRWIo9zHMobRNuRhiqLy1FOpB9FvqQsAwmA6D2CGYYaBm05xqJUFmRZ0JWfYcFRqu+zIdspQyeh8hKO8AyPIKeqjMKhHHJW+6Ua2TaKAEphGbRLl+m5Hm2eO9nwY5In9qZmWWdZXkIWuvCbP5KqyRhCCci0AzCqYXBeBpqivFaMW8vUXiGAE+kGsoXRdtMEHOcVKZZdZ2Bfj+0R-oB6DAdEYFOUV4nTaVnkrohfmoTSarBTUIotA02kfBprR6CKMVtGFurDEeI03mNPYTZti7be5s2gug4KxuxCLcVilB8ViuL4oQRIbS+33vjN9h5gFaHHUcjT3HUzJmEMurKDFvjmlywzmGTqgtC0XzdnD85IwG-6ZjGEDYAAQgAmgACrgOQ5LkuAAGJZOzKO1UFGP6oMJgtLF-J1DLhMaTyFhCpRlOch4aWQfT-qMz6kB4Kz6LZKDvGi4F6NKO09wGIEFh4RpwQxcEAyUfo8sy2oFha85I7jizuBG4iJs8eD5tozu+GtO4VqVlaA0eEEMUDedXAXFwvKctbRmxMgEA2eswekNQrCIuQAvkAAwjxqKUOHR2RwyZzMunVyEZW5o+B8HhtOYow52Aec2csqzyhsUn5g3ckVuduhtW1bRdK0pMxRpbhcJjtu+PoYo046utMwbI9yusioT6jU-1TPPLz2ndZmORXDGmpwxuHeO9p4KAT40ZvxgFIEgfLAkLtkEuZcK7VyxLXeu245IqFFBociopu4DQsIKQiJwBjvGMNgtQTZzC-xmP-QBkEADuyB-z-isqQCMX5RDQnsNKMcE4pyQnIZQqy1AvyIjANCGBdUTodXqAlfwNoghk0Im0LQ7gTCVhtEeLoHxCFYGIaIIB852FUPQDQuhDCmF2QcmwihWiuHRB4Xwjc0kI5yQMOadompF5WjbFoGKbUbabwNB2XwhhlGegAWoshxjqG0MSHo4EzC8qsI0Jozh3DeEcGUDVC2kcKIxxeBpKmZgzCEQMOdE4TQuQPUFBWXxqj1HRKCdokJ9DGHAkDsbYupdy5VxrnXc+YtLYIHxhdLknImyjD0HoQidY3AHg6OWdQ5Z7R7yiH-fx5SBCoH9DGewOjQm1JSBEwxf4lkrNMeY-h4sQq6HOqME4-JhQGAua454FpXB3C0OYJoyhSnzMgos5ZYBVnVLCZsgx+VIQfL2XEixSTrH1Q0oYLBxwqYnLTned4hF1CGAePHE5vhLRU1eSQ5yuciFvNEjlFh0ZYyOjmTi-seKVEEsWOVHyOYqrtOSXA92wiRRWi8KI2oBMTR1mkQadoxxzBXkotigJuLB74opeE-5USSTkvFZSyV1LpVQDpftKqljJ6wPqmMJs+4EWfBePbVxZYpbkRVgNOoNYxXlKpX41Vc1vy-gAkBUC4FZgKrtcqh1irkjqsqjwQ5nTORP2VsNK5yUVCrxlg8SmGltLGDbGYMI3YpBCAgHAWQvxDo6pOk4RQhFgiIN5IKIIvIN5WGpuNP48wgRQFzQI6oVNCIa2kY8gZgRLAGHerTRtRyEDxpvvPReD8V5ngrIodwPcuhGGOG2F5MyaImS2oxIM-bOltBtrfUdy8n4xXUOaR5146xpyNC0H2X0GLmR2huyOd9GRQvUPyO0XJVA9TrIMKwwpOo735JMJdxlnI6z1szO9cCPBTuMFTJ6ZhXB+EJtyD414KLtGrKlQD6U-aQHAxCtqAwDTlgrNYNsyVVL0h0JeHwGcqa+HLAPIeuGTofDal+1oacOPag+DFSw5oMUmFcFaGWRkD76wgEx6oZZiKlp0NpBNR4D0nKoyhusxwDS2rnBJq2KLm4nFbl0JF7sNCdSalFd4-g7waecjEqpuiNlaYao8xk5xWRtxNKkomVpMFCkefoKz-YgVfLWTUxYDn+QXgSiNZN3aW1njaiip+9zrBjEps8fzrp7VlM01Yy+gj9SzxrFaymlYrAtFNdC+55ETmVn8KmkIQA */
  id: "auth",
  context: {
    address: null,
    chainType: null,
    nearAccount: null,
    cachedSignedData: null,
  },

  invoke: {
    id: "authListenerRef",
    src: "authInvalidationListener",
  },

  on: {
    AUTH_INVALIDATED: {
      actions: "sendParentAuthInvalidated",
    },
  },

  initial: "restoring",
  states: {
    restoring: {
      on: {
        WALLET_STATE: [
          {
            guard: ({ event }) =>
              event.type === "WALLET_STATE" &&
              event.restoreStatus === "settled" &&
              event.address != null,
            target: "gating",
            actions: "setWalletFromEvent",
          },
          {
            guard: "isSettled",
            target: "ready",
            actions: "setWalletFromEvent",
          },
        ],
      },
      after: {
        RESTORE_TIMEOUT: {
          target: "ready",
          actions: "logRestoreTimeout",
        },
      },
    },

    gating: {
      on: {
        WALLET_STATE: [
          {
            guard: "hasWalletInEvent",
            target: "gating",
            reenter: true,
            actions: "setWalletFromEvent",
          },
          {
            guard: "isSettled",
            target: "blocked",
            actions: [
              "clearAuthJourneyLease",
              "clearWallet",
              "sendParentSignOut",
            ],
          },
        ],
      },
      initial: "checking_ban",
      states: {
        checking_ban: {
          after: {
            GATING_TIMEOUT: {
              target: "checking_safety",
              actions: "logGatingTimeout",
            },
          },
          invoke: {
            src: "banService",
            input: ({ context }) => ({
              address: context.address as string,
              chainType: context.chainType as string,
            }),
            onDone: [
              {
                guard: {
                  type: "isBanned",
                  params: ({ event }) => ({ output: event.output }),
                },
                target: "banned",
              },
              { target: "checking_safety" },
            ],
            onError: {
              target: "checking_safety",
              actions: {
                type: "logBanCheckUnavailable",
                params: ({ event }) => ({ error: event.error }),
              },
            },
          },
        },
        checking_safety: {
          after: {
            GATING_TIMEOUT: {
              target: "safety_unreachable",
              actions: "logSafetyCheckUnavailable",
            },
          },
          invoke: {
            src: "safetyService",
            input: ({ context }) => ({
              address: context.address as string,
              chainType: context.chainType as string,
            }),
            onDone: [
              {
                guard: {
                  type: "isUnsafe",
                  params: ({ event }) => ({ output: event.output }),
                },
                target: "safety_blocked",
              },
              { target: "#auth.ready" },
            ],
            onError: {
              target: "safety_unreachable",
              actions: "logSafetyCheckUnavailable",
            },
          },
        },
        safety_blocked: {
          on: {
            BYPASS_SAFETY: { target: "#auth.ready" },
            ABORT_GATING: {
              target: "#auth.blocked",
              actions: [
                {
                  type: "failAuthJourney",
                  params: {
                    failureStage: "connection",
                    failureReason: "cancelled",
                  },
                },
                "clearAuthJourneyLease",
                "clearWallet",
                "sendParentSignOut",
              ],
            },
          },
        },
        safety_unreachable: {
          on: {
            RETRY_SAFETY: { target: "checking_safety" },
            PROCEED_UNVERIFIED: { target: "#auth.ready" },
            ABORT_GATING: {
              target: "#auth.blocked",
              actions: [
                {
                  type: "failAuthJourney",
                  params: {
                    failureStage: "connection",
                    failureReason: "cancelled",
                  },
                },
                "clearAuthJourneyLease",
                "clearWallet",
                "sendParentSignOut",
              ],
            },
          },
        },
        banned: {
          entry: "sendParentBannedDetected",
          on: {
            ABORT_GATING: {
              target: "#auth.blocked",
              actions: [
                "clearAuthJourneyLease",
                "clearWallet",
                "sendParentSignOut",
              ],
            },
          },
        },
      },
    },

    ready: {
      entry: "sendParentWalletRestored",
      on: {
        START_VERIFICATION: {
          target: "authenticating",
          actions: "setVerificationTarget",
        },
        WALLET_STATE: {
          guard: "hasWalletInEvent",
          target: "gating",
          actions: "setWalletFromEvent",
        },
      },
    },

    blocked: {
      on: {
        WALLET_STATE: {
          guard: "hasWalletInEvent",
          target: "gating",
          actions: "setWalletFromEvent",
        },
      },
    },

    authenticating: {
      initial: "wallet_verifying",
      on: {
        START_VERIFICATION: {
          target: "authenticating",
          reenter: true,
          actions: ["clearPubkeyRecoveryState", "setVerificationTarget"],
        },
        WALLET_STATE: {
          guard: "hasWalletInEvent",
          target: "gating",
          actions: ["clearPubkeyRecoveryState", "setWalletFromEvent"],
        },
        ABORT_VERIFICATION: {
          target: "#auth.ready",
          actions: [
            {
              type: "failAuthJourney",
              params: {
                failureStage: "verification",
                failureReason: "cancelled",
              },
            },
            "clearPubkeyRecoveryState",
            "sendParentSignOut",
          ],
        },
      },
      states: {
        wallet_verifying: {
          invoke: {
            id: "walletVerRef",
            src: "appWalletVerification",
            onDone: [
              {
                guard: {
                  type: "isVerified",
                  params: ({ event }) => event.output,
                },
                target: "#auth.ready",
                actions: "sendParentVerificationComplete",
              },
              {
                guard: {
                  type: "hasFailedNearSignature",
                  params: ({ event }) => event.output,
                },
                target: "pubkey_verifying",
                actions: {
                  type: "captureFailedNearSignature",
                  params: ({ event }) => {
                    if (event.output.failedNearSignature == null) {
                      throw new Error(
                        "failedNearSignature required when hasFailedNearSignature is true"
                      )
                    }
                    return event.output.failedNearSignature
                  },
                },
              },
              {
                target: "#auth.ready",
                actions: [
                  {
                    type: "failAuthJourney",
                    params: {
                      failureStage: "verification",
                      failureReason: "verification_rejected",
                    },
                  },
                  "logWalletVerificationFailed",
                  "sendParentSignOut",
                ],
              },
            ],
          },
          on: {
            ABORT_VERIFICATION: {
              actions: sendTo("walletVerRef", { type: "ABORT" }),
            },
          },
        },
        pubkey_verifying: {
          invoke: {
            id: "pubkeyVerRef",
            src: "appPubKeyVerifier",
            input: ({ context }) => ({
              nearAccount: context.nearAccount,
              nearClient,
            }),
            onDone: [
              {
                guard: {
                  type: "isPubKeyOk",
                  params: ({ event }) => event.output,
                },
                target: "reauthenticating",
              },
              {
                target: "#auth.ready",
                actions: [
                  {
                    type: "failAuthJourney",
                    params: {
                      failureStage: "verification",
                      failureReason: "verification_rejected",
                    },
                  },
                  "logPubkeyVerificationFailed",
                  "clearPubkeyRecoveryState",
                  "sendParentSignOut",
                  "requestWalletDisconnect",
                ],
              },
            ],
          },
        },
        reauthenticating: {
          invoke: {
            src: "reauthAfterPubkey",
            input: ({ context }) => {
              if (
                context.cachedSignedData == null ||
                context.nearAccount == null
              ) {
                throw new Error(
                  "cachedSignedData/nearAccount must be set before reauthenticating"
                )
              }
              return {
                signedData: context.cachedSignedData,
                address: context.nearAccount.accountId,
                chainType: AuthMethod.Near,
              }
            },
            onDone: [
              {
                guard: {
                  type: "isReauthSuccess",
                  params: ({ event }) => event.output,
                },
                target: "#auth.ready",
                actions: [
                  "clearPubkeyRecoveryState",
                  "sendParentVerificationComplete",
                ],
              },
              {
                target: "#auth.ready",
                actions: [
                  {
                    type: "failAuthJourney",
                    params: {
                      failureStage: "verification",
                      failureReason: "verification_rejected",
                    },
                  },
                  "logReauthFailed",
                  "clearPubkeyRecoveryState",
                  "sendParentSignOut",
                  "requestWalletDisconnect",
                ],
              },
            ],
            onError: {
              target: "#auth.ready",
              actions: [
                {
                  type: "failAuthJourney",
                  params: {
                    failureStage: "verification",
                    failureReason: "verification_rejected",
                  },
                },
                {
                  type: "logReauthFailed",
                  params: ({ event }) => ({ error: event.error }),
                },
                "clearPubkeyRecoveryState",
                "sendParentSignOut",
                "requestWalletDisconnect",
              ],
            },
          },
        },
      },
    },
  },
})
